import { useState } from 'react';
import { FileFormatEnum } from 'types/generated/client';
import { useModal } from 'hooks/useModal';
import GalleryLeft from 'svg/GalleryLeft';
import GalleryRight from 'svg/GalleryRight';
import MoreGallery from 'svg/MoreGallery';
import CardsSection from 'components/CardSection';
import MediaLightbox from 'components/MediaLightbox';

interface File {
  url: string;
  format: FileFormatEnum;
}

interface Props {
  files: File[];
  maxPreviews: number;
  addMorePlaceholder?: React.ReactNode | null;
  showViewMoreButton: boolean;
}

export default function MediaGallery({
  files = [],
  maxPreviews,
  addMorePlaceholder,
  showViewMoreButton,
}: Props) {
  const { isOpen, openModal, closeModal } = useModal();
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleViewAll = () => {
    openModal();
  };

  const mediaJsx = (file: File, index: number) => (
    <div className="h-full overflow-hidden sm:rounded-md">
      {file.format === FileFormatEnum.Image ? (
        <img
          key={index}
          src={file.url}
          alt={`Preview ${index}`}
          className="h-full w-full shrink-0 object-cover  object-top"
        />
      ) : (
        <video
          key={index}
          src={file.url}
          controls
          className="h-full w-full shrink-0 object-cover object-top"
        />
      )}
    </div>
  );

  const extraMedia = files.length - maxPreviews;

  const moreJsx = (
    <span
      onClick={handleViewAll}
      className="absolute bottom-3 right-3 flex cursor-pointer items-center justify-center gap-1 rounded-full bg-color-bg-lightmode-primary px-4 py-2 text-size-product-button-label-small text-color-text-lightmode-primary dark:bg-color-bg-darkmode-primary dark:text-color-text-darkmode-primary"
    >
      <MoreGallery className="h-4 w-4" /> {extraMedia} more
    </span>
  );

  return (
    <div className="media-gallery">
      <div className="relative flex h-[22.5rem] gap-[1rem]">
        <CardsSection
          gap="1rem"
          className="w-full"
          data={files}
          renderCard={mediaJsx}
          visibleCards={{ base: maxPreviews }}
          showArrowsOnMobile
          prevArrow={
            <span className="absolute left-5 z-10 cursor-pointer select-none rounded-full bg-black/50 p-2">
              <GalleryLeft className="h-7 w-7 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
            </span>
          }
          nextArrow={
            <span className="absolute right-5 z-10 cursor-pointer select-none rounded-full bg-black/50 p-2">
              <GalleryRight className="h-7 w-7 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
            </span>
          }
        />
        {addMorePlaceholder}
        {showViewMoreButton && extraMedia > 0 && moreJsx}
      </div>

      {isOpen && (
        <MediaLightbox
          items={files}
          index={currentIndex}
          onIndexChange={setCurrentIndex}
          onClose={() => closeModal(true)}
        />
      )}
    </div>
  );
}
